import { useCallback, useEffect, useState } from 'react';
import './AccountManager.css';
import { authFetch } from '../auth';

const API_BASE = '/api';

const PERMISSIONS = [
  { value: 1, label: '통합관리자' },
  { value: 5, label: '컨텐츠 관리자' },
  { value: 9, label: '계정 정지' },
];
const permLabel = (v) => {
  const found = PERMISSIONS.find((p) => p.value === Number(v));
  return found ? found.label : v;
};

// 정지(9) 계정은 dateEdit + 30일에 자동 삭제 → 남은 일자 계산
const RETENTION_DAYS = 30;
const remainingDays = (dateEdit) => {
  if (!dateEdit) return null;
  const base = new Date(dateEdit.replace(' ', 'T'));
  if (isNaN(base.getTime())) return null;
  const deleteAt = base.getTime() + RETENTION_DAYS * 86400000;
  return Math.max(0, Math.ceil((deleteAt - Date.now()) / 86400000));
};

const EMPTY_FORM = { strID: '', strPW: '', charName: '', intPermission: 1 };

export default function AccountManager() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null=신규, 객체=수정
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await authFetch(`${API_BASE}/accounts`);
      if (!res.ok) throw new Error(`목록 조회 실패 (${res.status})`);
      setAccounts(await res.json());
    } catch (e) {
      setError(`${e.message} — 백엔드(8080)가 실행 중인지 확인하세요.`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormError('');
    setFormOpen(true);
  };

  const openEdit = (a) => {
    setEditing(a);
    setForm({
      strID: a.strID || '',
      strPW: '',
      charName: a.charName || '',
      intPermission: a.intPermission ?? 1,
    });
    setFormError('');
    setFormOpen(true);
  };

  const closeForm = () => {
    if (saving) return;
    setFormOpen(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.strID.trim()) return setFormError('ID(이메일)를 입력하세요.');
    if (!editing && !form.strPW) return setFormError('비밀번호를 입력하세요.');

    setSaving(true);
    setFormError('');
    const payload = {
      strID: form.strID.trim(),
      strPW: form.strPW,
      charName: form.charName,
      intPermission: Number(form.intPermission) || 0,
    };
    try {
      const url = editing ? `${API_BASE}/accounts/${editing.intNum}` : `${API_BASE}/accounts`;
      const method = editing ? 'PUT' : 'POST';
      const res = await authFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        let msg = `저장 실패 (${res.status})`;
        try {
          const body = await res.json();
          if (body.message) msg = body.message;
        } catch (_) {}
        throw new Error(msg);
      }
      setFormOpen(false);
      await load();
    } catch (e2) {
      setFormError(e2.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (a) => {
    if (a.intPermission === 9) {
      alert('이미 정지된 계정입니다.');
      return;
    }
    if (!window.confirm(`'${a.strID}' 계정을 삭제(정지)할까요?\n실제로 삭제되지 않고 권한이 9(계정 정지)로 변경되어 로그인할 수 없게 됩니다.`)) return;
    try {
      const res = await authFetch(`${API_BASE}/accounts/${a.intNum}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`처리 실패 (${res.status})`);
      await load();
    } catch (e) {
      alert(e.message);
    }
  };

  return (
    <div className="acct">
      <div className="acct-head">
        <div>
          <h1 className="acct-title">계정 관리</h1>
          <p className="acct-sub">관리자 계정을 추가·수정·삭제합니다.</p>
        </div>
        <button type="button" className="acct-btn-dark" onClick={openCreate}>+ 계정 추가</button>
      </div>

      {error && <div className="acct-error">{error} <button type="button" onClick={load}>다시 시도</button></div>}

      <div className="acct-card">
        <table className="acct-table">
          <thead>
            <tr>
              <th style={{ width: 70 }}>번호</th>
              <th>ID (이메일)</th>
              <th style={{ width: 130 }}>이름</th>
              <th style={{ width: 185 }}>권한</th>
              <th style={{ width: 175 }}>등록일</th>
              <th style={{ width: 150 }}>작업</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="acct-empty">불러오는 중…</td></tr>
            ) : accounts.length === 0 ? (
              <tr><td colSpan={6} className="acct-empty">등록된 계정이 없습니다.</td></tr>
            ) : (
              accounts.map((a) => (
                <tr key={a.intNum}>
                  <td>{a.intNum}</td>
                  <td>{a.strID}</td>
                  <td>{a.charName}</td>
                  <td>
                    {a.intPermission === 9 ? (
                      <span className="acct-badge perm-9">계정 정지 · {remainingDays(a.dateEdit)}일 남음</span>
                    ) : (
                      <span className={`acct-badge perm-${a.intPermission}`}>{permLabel(a.intPermission)}</span>
                    )}
                  </td>
                  <td className="acct-date">{a.dateReg}</td>
                  <td>
                    <button type="button" className="acct-btn-sm" onClick={() => openEdit(a)}>수정</button>
                    <button type="button" className="acct-btn-sm danger" onClick={() => remove(a)}>삭제</button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {formOpen && (
        <div className="acct-modal-overlay" onMouseDown={closeForm}>
          <div className="acct-modal" onMouseDown={(e) => e.stopPropagation()}>
            <h2 className="acct-modal-title">{editing ? '계정 수정' : '계정 추가'}</h2>
            <form onSubmit={submit}>
              <div className="acct-fg">
                <label>ID (이메일)</label>
                <input type="text" value={form.strID} autoComplete="off"
                  onChange={(e) => setForm({ ...form, strID: e.target.value })} />
              </div>
              <div className="acct-fg">
                <label>비밀번호 {editing && <span className="acct-hint">(변경 시에만 입력)</span>}</label>
                <input
                  type="password"
                  value={form.strPW}
                  autoComplete="new-password"
                  placeholder={editing ? '변경하지 않으려면 비워두세요' : ''}
                  onChange={(e) => setForm({ ...form, strPW: e.target.value })}
                />
              </div>
              <div className="acct-fg">
                <label>이름</label>
                <input type="text" value={form.charName}
                  onChange={(e) => setForm({ ...form, charName: e.target.value })} />
              </div>
              <div className="acct-fg">
                <label>권한</label>
                <select className="acct-select" value={form.intPermission}
                  onChange={(e) => setForm({ ...form, intPermission: Number(e.target.value) })}>
                  {PERMISSIONS.map((p) => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
              </div>

              {formError && <p className="acct-form-error">{formError}</p>}

              <div className="acct-modal-actions">
                <button type="button" className="acct-btn-ghost" onClick={closeForm} disabled={saving}>취소</button>
                <button type="submit" className="acct-btn-dark" disabled={saving}>
                  {saving ? '저장 중…' : '저장'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
